import UnsavedChangesBoundary from "./components/common/UnsavedChangesBoundary";
import React, { useEffect, useId, useMemo, useState } from "react";
import { z } from "zod";
import { createRisk, getRiskCategories } from "./riskService";
import FormWithValidation from "./components/common/FormWithValidation";
import Input from "./ui/Input";
import Select from "./ui/Select";
import ModalA11yWrapper from "./ui/ModalA11yWrapper";
import { spacing, typography } from "./ui/tokens";

const buildSchema = (requireCategory) =>
  z.object({
    name: z.string().min(1, "Name is required."),
    category: requireCategory ? z.string().min(1, "Category is required.") : z.string().optional().nullable(),
    subcategory: z.string().optional().nullable(),
    likelihood: z
      .number({ invalid_type_error: "Likelihood must be an integer between 1 and 5." })
      .int("Likelihood must be an integer between 1 and 5.")
      .min(1, "Likelihood must be an integer between 1 and 5.")
      .max(5, "Likelihood must be an integer between 1 and 5."),
    impact: z
      .number({ invalid_type_error: "Impact must be an integer between 1 and 5." })
      .int("Impact must be an integer between 1 and 5.")
      .min(1, "Impact must be an integer between 1 and 5.")
      .max(5, "Impact must be an integer between 1 and 5."),
  });

export default function AddChildRiskForm({ parentId, onClose, onSaved }) {
  const [loading, setLoading] = useState(false);
  const [formError, setFormError] = useState(null);
  const [categories, setCategories] = useState([]);
  const [categoriesLoading, setCategoriesLoading] = useState(true);
  const [categoriesError, setCategoriesError] = useState(null);
  const [serverFieldErrors, setServerFieldErrors] = useState({});
  const defaultValues = useMemo(() => ({
    name: "",
    category: "",
    subcategory: "",
    // valueAsNumber represents an empty number input as NaN.
    likelihood: NaN,
    impact: NaN,
  }), []);
  const titleId = useId();

  useEffect(() => {
    setCategoriesLoading(true);
    setCategoriesError(null);
    getRiskCategories({ include_archived: false })
      .then((res) => {
        const items = res?.data || res || [];
        setCategories(items);
      })
      .catch((err) => {
        setCategories([]);
        setCategoriesError(err?.response?.data?.error || err?.message || "Unable to load risk categories.");
      })
      .finally(() => setCategoriesLoading(false));
  }, []);

  const activeCategories = useMemo(
    () => (categories || []).filter((c) => (c?.status || "active") === "active"),
    [categories]
  );
  const canSelectCategory = activeCategories.length > 0;
  const schema = useMemo(() => buildSchema(canSelectCategory), [canSelectCategory]);

  const handleSave = async (values) => {
    setLoading(true);
    setFormError(null);
    setServerFieldErrors({});
    if (!canSelectCategory) {
      setFormError("Risk categories are not configured. Contact an administrator to set them up.");
      setLoading(false);
      return;
    }

    try {
      await createRisk({
        name: values.name.trim(),
        category: values.category?.trim() || "",
        subcategory: values.subcategory?.trim() || null,
        likelihood: values.likelihood,
        impact: values.impact,
        parent_id: parentId, // key
        status: "Draft",
      });

      onSaved?.();
      onClose?.();
    } catch (err) {
      setServerFieldErrors(err?.fieldErrors || err?.response?.data?.field_errors || {});
      setFormError(err?.response?.data?.detail || err?.message || "Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <UnsavedChangesBoundary onClose={onClose} itemName="risk">
      {(requestClose) => (
    <ModalA11yWrapper open onClose={requestClose} labelledBy={titleId}>
      <div
        style={{
          width: 420,
          background: "white",
          padding: spacing.lg * 2,
          borderRadius: 12,
          boxShadow: "0 12px 30px rgba(0,0,0,0.2)",
        }}
      >
        <h3 id={titleId} style={{ marginTop: 0, fontSize: typography.size.lg }}>Add Child Risk</h3>

        <FormWithValidation
          schema={schema}
          defaultValues={defaultValues}
          onSubmit={handleSave}
          onCancel={requestClose}
          submitLabel={loading ? "Saving..." : "Save"}
          submitDisabled={(formState) =>
            loading || categoriesLoading || !canSelectCategory || !formState.isValid
          }
          formError={formError}
          serverFieldErrors={serverFieldErrors}
        >
          {({ register, formState }) => (
            <div>
              <Input
                label="Risk name"
                placeholder="Risk name"
                error={formState.errors.name?.message}
                style={inputStyle}
                {...register("name")}
              />
              <label style={fieldLabel}>
                <span style={labelText}>Category</span>
                <Select
                  style={inputStyle}
                  error={formState.errors.category?.message}
                  disabled={!canSelectCategory}
                  {...register("category")}
                >
                  <option value="">Select category</option>
                  {activeCategories.map((c) => (
                    <option key={c.name} value={c.name}>
                      {c.name}
                    </option>
                  ))}
                </Select>
                {!canSelectCategory && !categoriesLoading && !categoriesError && (
                  <span style={helperText}>No active categories are available.</span>
                )}
              </label>
              <Input
                label="Subcategory"
                placeholder="Subcategory"
                style={inputStyle}
                {...register("subcategory")}
              />
              <Input
                label="Likelihood (1-5)"
                type="number"
                min={1}
                max={5}
                step={1}
                error={formState.errors.likelihood?.message}
                style={inputStyle}
                {...register("likelihood", { valueAsNumber: true })}
              />
              <Input
                label="Impact (1-5)"
                type="number"
                min={1}
                max={5}
                step={1}
                error={formState.errors.impact?.message}
                style={inputStyle}
                {...register("impact", { valueAsNumber: true })}
              />
              {categoriesLoading && <div className="loading">Loading categories...</div>}
              {categoriesError && <div style={{ color: "#b91c1c" }}>{categoriesError}</div>}
              {loading && <div className="loading">Loading...</div>}
            </div>
          )}
        </FormWithValidation>
      </div>
    </ModalA11yWrapper>
      )}
    </UnsavedChangesBoundary>
  );
}

const inputStyle = {
  width: "100%",
  marginBottom: spacing.lg,
  padding: `${spacing.md}px ${spacing.lg}px`,
  borderRadius: 6,
  border: "1px solid #ccc",
};

const fieldLabel = {
  display: "flex",
  flexDirection: "column",
  gap: spacing.xs,
};

const labelText = {
  fontSize: typography.size.sm,
  color: "#374151",
};

const helperText = {
  fontSize: typography.size.xs,
  color: "#6b7280",
};
